/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    ITelemetryContent,
    TelemetryConstant,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-view-mode-selector';
const listViewClassName = `${componentClassName}__list-view`;
const viewSelectedClassName = `${componentClassName}__selected`;

/**
 * IOrderHistoryWithSearchAndFiltersViewModeResources interface.
 */
export interface IOrderHistoryWithSearchAndFiltersViewModeResources {
    listViewOptionAriaLabel?: string;
    detailedViewOptionAriaLabel?: string;
}

/**
 * OrderHistoryWithSearchAndFiltersViewModeState interface.
 */
export enum OrderHistoryWithSearchAndFiltersViewModeState {
    DetailedView,
    ListView
}

/**
 * IOrderHistoryWithSearchAndFiltersViewModeProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersViewModeProps extends IComponentProps<{}> {
    className?: string;
    resources?: IOrderHistoryWithSearchAndFiltersViewModeResources;
    currentViewModeState: OrderHistoryWithSearchAndFiltersViewModeState;
    telemetryContent?: ITelemetryContent;
    onClick(state: OrderHistoryWithSearchAndFiltersViewModeState): void;
}

/**
 * OrderHistoryWithSearchAndFiltersViewMode component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersViewModeFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersViewModeProps> = (
    props: IOrderHistoryWithSearchAndFiltersViewModeProps
) => {
    const [viewModeState, setViewModeState] = React.useState(props.currentViewModeState);

    const onClickHandler = (newViewModeState: OrderHistoryWithSearchAndFiltersViewModeState) => {
        setViewModeState(newViewModeState);
        props.onClick(newViewModeState);
    };

    // define telemetry attributes
    const viewModeListViewAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, `${TelemetryConstant.ViewMode} - List view`)
    );

    return (
        <div className={classnames(props.className, componentClassName)}>
            <Button
                className={classnames(
                    listViewClassName,
                    viewModeState === OrderHistoryWithSearchAndFiltersViewModeState.ListView ? viewSelectedClassName : ''
                )}
                aria-label={props.resources?.listViewOptionAriaLabel ?? ''}
                title={props.resources?.listViewOptionAriaLabel ?? ''}
                role='button'
                onClick={() => onClickHandler(OrderHistoryWithSearchAndFiltersViewModeState.ListView)}
                {...viewModeListViewAttributes}
            />
        </div>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersViewMode component.
 */
export const OrderHistoryWithSearchAndFiltersViewModeComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersViewModeProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersViewModeProps
>('OrderHistoryWithSearchAndFiltersViewModeComponent', { component: OrderHistoryWithSearchAndFiltersViewModeFunctionalComponent });
